import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useCollectPayment } from '../../hooks/useCollectPayment';
import { TranslationKey } from '../../i18n/translationKeys';
import { useRelanceDeactivationStore } from '../../stores/relanceDeactivationStore';
import { useToastStore } from '../../stores/toastStore';
import { ActionMenu } from '../../ui/ActionMenu/ActionMenu';
import type { ActionMenuItem } from '../../ui/ActionMenu/types';
import type { RelanceAgendaEntry } from '../../types/customerFollowUp/relanceAgenda';
import { IgnoreRelanceModal } from './IgnoreRelanceModal';
import { RelanceActionModal } from './RelanceActionModal';
import { collectableRelance } from './collectableRelance';

interface RelanceRowMenuProps {
    entry: RelanceAgendaEntry;
    canCollectPayment: boolean;
    testIdPrefix: string;
}

/** The row's "⋮" shortcuts to its actions. A sent relance has none, so its menu is disabled; an expected one can only be collected. */
export const RelanceRowMenu: React.FC<RelanceRowMenuProps> = ({ entry, canCollectPayment, testIdPrefix }) => {
    const { t } = useTranslation();
    const { item, status } = entry;
    const deactivateTask = useRelanceDeactivationStore(s => s.deactivateTask);
    const showToast = useToastStore(s => s.showToast);
    const collectPayment = useCollectPayment();
    const [modal, setModal] = useState<'ignore' | 'action' | null>(null);

    const collectItem: ActionMenuItem[] = canCollectPayment ? [{
        label: t(TranslationKey.Home.PaidAction.Tooltip),
        onSelect: () => { if (!collectPayment(collectableRelance(entry))) showToast('error', t(TranslationKey.Home.PaidAction.UnreachableError)); },
        testId: `${testIdPrefix}-menu-paid-${item.id}`,
    }] : [];
    const ignoreItem: ActionMenuItem = { label: t(TranslationKey.Home.Agenda.MenuIgnore), onSelect: () => setModal('ignore'), testId: `${testIdPrefix}-menu-ignore-${item.id}` };
    const reactivateItem = (label: string): ActionMenuItem => ({ label, onSelect: () => setModal('action'), testId: `${testIdPrefix}-menu-reactivate-${item.id}` });

    const itemsByStatus: Record<RelanceAgendaEntry['status'], () => ActionMenuItem[]> = {
        due: () => [ignoreItem, ...collectItem],
        snoozed: () => [reactivateItem(t(TranslationKey.Home.ActionModal.BringForward)), ...collectItem, ignoreItem],
        ignored: () => [reactivateItem(t(TranslationKey.Home.Deactivated.Row.Reactivate)), ...collectItem],
        sent: () => [],
        expected: () => collectItem,
    };

    return (
        <>
            <ActionMenu items={itemsByStatus[status]()} ariaLabel={t(TranslationKey.Home.Agenda.MenuLabel)} testId={`${testIdPrefix}-menu-${item.id}`} />
            {modal === 'ignore' && (
                <IgnoreRelanceModal
                    taskId={item.id}
                    customerName={item.title}
                    formattedAmount={item.amount}
                    initialNote={entry.note}
                    onCancel={() => setModal(null)}
                    onConfirm={async submission => {
                        if (await deactivateTask(item.id, submission)) setModal(null);
                        else showToast('error', t(TranslationKey.ApiErrors.Generic));
                    }}
                />
            )}
            {modal === 'action' && (
                <RelanceActionModal entry={entry} canCollectPayment={canCollectPayment} onClose={() => setModal(null)} />
            )}
        </>
    );
};
